import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, IconButton, Collapse,
  Paper, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Button,
} from "@mui/material";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { axiosPartners } from "../../axios/axiosConfig";
import toast from "react-hot-toast";

const fmt = (dateStr) =>
  dateStr
    ? new Date(dateStr).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "2-digit",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";

const orderStatusColor = (status) => {
  const s = status?.toUpperCase() || "";
  if (s.includes("GREEN")) return { bg: "#dcfce7", color: "#16a34a" };
  if (s.includes("RED"))   return { bg: "#fee2e2", color: "#dc2626" };
  if (s === "PENDING")     return { bg: "#fef9c3", color: "#b45309" };
  if (s === "CANCEL" || s === "CANCELLED") return { bg: "#fee2e2", color: "#dc2626" };
  return { bg: "#f1f5f9", color: "#64748b" };
};

const signalColor = (signal) =>
  signal?.toLowerCase() === "buy"
    ? { bg: "#dcfce7", color: "#16a34a" }
    : { bg: "#fee2e2", color: "#dc2626" };

// ─── Sub-table: all trades for a single symbol ───────────────────────────────
const SymbolTradesTable = ({ trades }) => (
  <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden", mt: 0.5 }}>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ background: "#f8fafc" }}>
          {["#", "Client ID", "Broker", "Type", "Qty", "Green Price", "Exec. Green", "Red Alert", "Exec. Red", "Status", "Day H/L", "Exchange", "Created At"].map((h) => (
            <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", py: 1, whiteSpace: "nowrap" }}>{h}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {trades.length === 0 ? (
          <TableRow>
            <TableCell colSpan={13} sx={{ textAlign: "center", py: 2, fontSize: 12, color: "#94a3b8" }}>No trades</TableCell>
          </TableRow>
        ) : trades.map((t, i) => {
          const sc = orderStatusColor(t.order_status);
          const sg = signalColor(t.entry_signal);
          return (
            <TableRow key={t.id ?? i} sx={{ "&:hover": { background: "#f8fafc" } }}>
              <TableCell sx={{ fontSize: 12, color: "#94a3b8" }}>{i + 1}</TableCell>
              <TableCell sx={{ fontSize: 12, color: "#374151", fontWeight: 600 }}>{t.client_id}</TableCell>
              <TableCell sx={{ fontSize: 12, color: "#374151" }}>{t.broker_name || "—"}</TableCell>
              <TableCell>
                <Chip label={t.entry_signal} size="small" sx={{ background: sg.bg, color: sg.color, fontWeight: 700, fontSize: 10, height: 20, borderRadius: "4px" }} />
              </TableCell>
              <TableCell sx={{ fontSize: 12, color: "#374151" }}>{t.green_quantity ?? "—"}</TableCell>
              <TableCell sx={{ fontSize: 12, color: "#374151", whiteSpace: "nowrap" }}>
                {t.green_price > 0 ? `₹${t.green_price}` : "—"}
              </TableCell>
              <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#16a34a", whiteSpace: "nowrap" }}>
                {t.executed_green_price != null ? `₹${t.executed_green_price}` : "—"}
              </TableCell>
              <TableCell sx={{ fontSize: 12, color: "#374151", whiteSpace: "nowrap" }}>
                {t.red_alert_price > 0 ? `₹${t.red_alert_price}` : "—"}
              </TableCell>
              <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#dc2626", whiteSpace: "nowrap" }}>
                {t.executed_red_price != null ? `₹${t.executed_red_price}` : "—"}
              </TableCell>
              <TableCell>
                <Chip label={t.order_status} size="small" sx={{ background: sc.bg, color: sc.color, fontWeight: 700, fontSize: 10, height: 20, borderRadius: "4px" }} />
              </TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <Typography sx={{ fontSize: 11 }}><span style={{ color: "#16a34a", fontWeight: 600 }}>H</span> {t.day_high ?? "—"}</Typography>
                <Typography sx={{ fontSize: 11 }}><span style={{ color: "#dc2626", fontWeight: 600 }}>L</span> {t.day_low ?? "—"}</Typography>
              </TableCell>
              <TableCell sx={{ fontSize: 11, color: "#64748b" }}>{t.exchange_segment || "—"}</TableCell>
              <TableCell sx={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap" }}>{fmt(t.created_at)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

// ─── Symbol group row ────────────────────────────────────────────────────────
const SymbolRow = ({ symbolName, count, trades, index, onExit, exitLoading }) => {
  const [expanded, setExpanded] = useState(false);

  const buyCount  = trades.filter((t) => t.entry_signal?.toLowerCase() === "buy").length;
  const sellCount = trades.length - buyCount;

  const statusCounts = trades.reduce((acc, t) => {
    const s = t.overall_status || "UNKNOWN";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const tradingSymbol = trades[0]?.trading_symbol || "";
  const exchange      = trades[0]?.exchange_segment || "";
  const dayHigh       = trades[0]?.day_high;
  const dayLow        = trades[0]?.day_low;

  return (
    <>
      <TableRow
        onClick={() => setExpanded((p) => !p)}
        sx={{ cursor: "pointer", "&:hover": { background: "#f8fafc" }, transition: "background 0.15s" }}
      >
        {/* # */}
        <TableCell sx={{ fontSize: 12, color: "#94a3b8", py: 1.5 }}>{index + 1}</TableCell>

        {/* Symbol */}
        <TableCell sx={{ py: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#04343a", lineHeight: 1.3 }}>{symbolName}</Typography>
          <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{tradingSymbol} · {exchange}</Typography>
        </TableCell>

        {/* Count */}
        <TableCell sx={{ py: 1.5 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#0f172a" }}>{count}</Typography>
          <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>trades</Typography>
        </TableCell>

        {/* Buy / Sell */}
        <TableCell sx={{ py: 1.5 }}>
          <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap" }}>
            <Chip label={`Buy ${buyCount}`}  size="small" sx={{ background: "#dcfce7", color: "#16a34a", fontWeight: 700, fontSize: 11, height: 22, borderRadius: "6px" }} />
            <Chip label={`Sell ${sellCount}`} size="small" sx={{ background: "#fee2e2", color: "#dc2626", fontWeight: 700, fontSize: 11, height: 22, borderRadius: "6px" }} />
          </Box>
        </TableCell>

        {/* Status breakdown */}
        <TableCell sx={{ py: 1.5 }}>
          <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap" }}>
            {Object.entries(statusCounts).map(([s, cnt]) => {
              const c = orderStatusColor(s);
              return (
                <Chip key={s} label={`${s} (${cnt})`} size="small"
                  sx={{ background: c.bg, color: c.color, fontWeight: 700, fontSize: 10, height: 20, borderRadius: "4px" }} />
              );
            })}
          </Box>
        </TableCell>

        {/* Day H/L */}
        <TableCell sx={{ py: 1.5, whiteSpace: "nowrap" }}>
          <Typography sx={{ fontSize: 11 }}><span style={{ color: "#16a34a", fontWeight: 600 }}>H</span> {dayHigh ?? "—"}</Typography>
          <Typography sx={{ fontSize: 11 }}><span style={{ color: "#dc2626", fontWeight: 600 }}>L</span> {dayLow ?? "—"}</Typography>
        </TableCell>

        {/* Actions */}
        <TableCell sx={{ py: 1.5 }}>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onExit(symbolName); }}
            disabled={exitLoading}
            sx={{ color: "#dc2626", "&:hover": { background: "#fef2f2" }, "&:disabled": { color: "#fca5a5" } }}
            title={`Exit all trades for ${symbolName}`}
          >
            {exitLoading
              ? <CircularProgress size={16} sx={{ color: "#dc2626" }} />
              : <ExitToAppIcon fontSize="small" />}
          </IconButton>
        </TableCell>

        {/* Expand */}
        <TableCell sx={{ py: 1.5 }}>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); setExpanded((p) => !p); }}
            sx={{ color: "#016551", "&:hover": { background: "#f0fdf4" } }}
          >
            {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
      </TableRow>

      {/* Expanded trades */}
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, border: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ px: 3, py: 1.5, background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", mb: 1, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Trades for {symbolName} ({trades.length})
              </Typography>
              <SymbolTradesTable trades={trades} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ─── Main page ───────────────────────────────────────────────────────────────
const TradeDashboardSymbolwise = () => {
  const [rawData,        setRawData]        = useState({});
  const [loading,        setLoading]        = useState(true);
  const [confirmSymbol,  setConfirmSymbol]  = useState(null);   // symbol pending exit confirmation
  const [exitingSymbols, setExitingSymbols] = useState(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosPartners.get("/brokers/open-trade-dashboard-symbolwise/");
      setRawData(res.data?.response ?? {});
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Failed to load symbolwise dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExitConfirm = async () => {
    const symbol = confirmSymbol;
    setConfirmSymbol(null);
    setExitingSymbols((prev) => new Set(prev).add(symbol));
    try {
      const res = await axiosPartners.post(`/brokers/exit-stock-order-by-symbol/${symbol}/`);
      toast.success(res.data?.message || `Exit successful for ${symbol}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || `Failed to exit trades for ${symbol}`);
    } finally {
      setExitingSymbols((prev) => { const s = new Set(prev); s.delete(symbol); return s; });
    }
  };

  const symbolGroups = useMemo(() =>
    Object.entries(rawData).map(([symbolName, { count, data }]) => ({
      symbolName, count: data.length, trades: data,
    }))
      .filter((g) => g.trades.length > 0)
      .sort((a, b) => b.count - a.count),
  [rawData]);

  const allTrades    = symbolGroups.flatMap((g) => g.trades);
  const buyCount     = allTrades.filter((t) => t.entry_signal?.toLowerCase() === "buy").length;
  const sellCount    = allTrades.length - buyCount;

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>

      {/* Header */}
      <Box sx={{ background: "#04343a", borderRadius: "12px", px: 3, py: 2, mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 4px 20px rgba(4,52,58,0.3)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <ShowChartIcon sx={{ color: "#eef2f7", fontSize: 26 }} />
          <Box>
            <Typography sx={{ color: "#ffffff", fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>Trade Dashboard — Symbolwise</Typography>
            <Typography sx={{ color: "#eef2f7", fontSize: 12 }}>Open trades grouped by symbol</Typography>
          </Box>
        </Box>
      </Box>

      {/* Table */}
      <Box sx={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <Box sx={{ background: "#04343a", px: 3, py: 1.5 }}>
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>
            {symbolGroups.length} Symbol{symbolGroups.length !== 1 ? "s" : ""}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: "#016551" }} size={36} />
          </Box>
        ) : symbolGroups.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>No trades found</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "#f8fafc" }}>
                  {["#", "Symbol", "Count", "Buy / Sell", "Order Status", "Day H/L", "Exit", "Details"].map((h) => (
                    <TableCell key={h} sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", py: 1.5, borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {symbolGroups.map((group, i) => (
                  <SymbolRow
                    key={group.symbolName}
                    index={i}
                    symbolName={group.symbolName}
                    count={group.count}
                    trades={group.trades}
                    onExit={setConfirmSymbol}
                    exitLoading={exitingSymbols.has(group.symbolName)}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Exit confirmation dialog */}
      <Dialog open={!!confirmSymbol} onClose={() => setConfirmSymbol(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#04343a" }}>Exit All Trades — {confirmSymbol}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            Are you sure you want to exit <strong>all open trades</strong> for symbol <strong>{confirmSymbol}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmSymbol(null)} sx={{ color: "#64748b" }}>Cancel</Button>
          <Button
            onClick={handleExitConfirm}
            variant="contained"
            sx={{ background: "#dc2626", "&:hover": { background: "#b91c1c" }, fontWeight: 700 }}
          >
            Exit Trades
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TradeDashboardSymbolwise;
